import { useState, useEffect, useMemo, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, Hammer, Handshake, Plus, Server, StickyNote, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { modpackNotesApiService } from '../../services/modpackNotesApi';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { CreatorProfile } from '../CreatorProfile';
import { SkeletonLoader } from '../SkeletonLoader';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { SectionHeader } from '../ui/SectionHeader';
import { cn } from '../../utils/cn';
import { ModpackNote } from '../../types/modpackNote';
import { PartnershipModpack, Partnership } from '../../types/partnership';
import { BuildModpackLink } from '../../types/buildTeam';
import { serverListApiService, ServerListEntry } from '../../services/serverListApi';
import { modpackConfigsApiService } from '../../services/modpackConfigsApi';
import type { ModpackConfig } from '../../types/modpackConfig';
import { canViewModpackConfigs } from '../../utils/modpackConfigPermissions';
import { getPartnershipPerms } from '../../utils/partnershipPermissions';

interface PackCrossModuleSectionProps {
  packName: string;
  packSlug: string;
  /** Modpack Index id; null for CF-primary packs not matched to MPI (server lookup skipped). */
  mpiId: number | null;
  onCreatePartnership?: () => void;
  onCreateModpackNote?: () => void;
}

// ── Modpack Notes Sub-Section ───────────────────────────────────────────────

const ModpackNotesIntegration = memo(({
  packName,
  packSlug,
  canView,
  canWrite,
  onCreateNote,
}: {
  packName: string;
  packSlug: string;
  canView: boolean;
  canWrite: boolean;
  onCreateNote?: () => void;
}) => {
  const [notes, setNotes] = useState<ModpackNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!canView) return;
    setLoading(true);
    // Search notes for this pack by name
    modpackNotesApiService.searchNotes(packName, 1, 10)
      .then((res) => {
        setNotes(res.results || []);
      })
      .catch(() => {
        // Also try by slug
        modpackNotesApiService.searchNotes(packSlug, 1, 10)
          .then((res) => setNotes(res.results || []))
          .catch(() => setNotes([]));
      })
      .finally(() => setLoading(false));
  }, [packName, packSlug, canView]);

  if (!canView) return null;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Modpack Notes"
        icon={StickyNote}
        iconClassName="text-yellow-400"
        count={!loading && notes.length > 0 ? notes.length : undefined}
        actions={
          <>
            {canWrite && onCreateNote && (
              <button onClick={onCreateNote} className="glass-button-sm">
                <Plus className="w-3.5 h-3.5" />
                Add Note
              </button>
            )}
            <Link to="/modpack-notes" className="glass-button-secondary btn-xs">
              <ExternalLink className="w-3 h-3" />
              View All
            </Link>
          </>
        }
      />

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          <SkeletonLoader className="h-16 w-full rounded-lg" />
          <SkeletonLoader className="h-16 w-full rounded-lg" />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-glass-400">
          No modpack notes found for &ldquo;{packName}&rdquo;.
          {canWrite && ' Click "Add Note" to create one.'}
        </p>
      ) : (
        <div className="space-y-2">
          {(expanded ? notes : notes.slice(0, 3)).map((note) => (
            <div key={note.id} className="subcard p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <CreatorProfile creatorId={note.creator_id} />
                  {note.cluster_id && (
                    <span className="badge badge-info">
                      {note.cluster_id}
                    </span>
                  )}
                  {note.fqdn && (
                    <span className="font-mono text-glass-400 truncate">{note.fqdn}</span>
                  )}
                </div>
                <span className="text-glass-500 tabular-nums shrink-0">
                  {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                </span>
              </div>
              <div className="text-sm text-glass-300 line-clamp-3">
                <MarkdownRenderer content={note.content} />
              </div>
            </div>
          ))}
          {notes.length > 3 && !expanded && (
            <button
              onClick={() => setExpanded(true)}
              className="text-xs text-accent-300 hover:text-accent-200"
            >
              Show {notes.length - 3} more note{notes.length - 3 > 1 ? 's' : ''}...
            </button>
          )}
        </div>
      )}
    </div>
  );
});

ModpackNotesIntegration.displayName = 'ModpackNotesIntegration';

// ── Partnerships Sub-Section ────────────────────────────────────────────────

const PartnershipsIntegration = memo(({
  packName,
  packSlug,
  mpiId,
  canView,
  canCreate,
  onCreatePartnership,
}: {
  packName: string;
  packSlug: string;
  mpiId: number | null;
  canView: boolean;
  canCreate: boolean;
  onCreatePartnership?: () => void;
}) => {
  const navigate = useNavigate();
  const [modpacks, setModpacks] = useState<PartnershipModpack[]>([]);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [cduServers, setCduServers] = useState<ServerListEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canView) return;
    setLoading(true);

    const load = async () => {
      // Parallel: search partnerships by name AND get CDU servers for this pack
      const [modpackResults, servers] = await Promise.allSettled([
        partnershipsApiService.searchModpacks(packName, 1, 10),
        mpiId != null
          ? serverListApiService.getServersByMpiId(mpiId)
          : Promise.resolve([] as ServerListEntry[]),
      ]);

      // Partnership modpacks matching by name
      const mps = modpackResults.status === 'fulfilled'
        ? (Array.isArray(modpackResults.value) ? modpackResults.value : [])
        : [];
      setModpacks(mps);

      // CDU servers running this pack
      const srvs = servers.status === 'fulfilled' ? servers.value : [];
      setCduServers(srvs);

      // Fetch partnerships for each matched modpack
      if (mps.length > 0) {
        const allPartnerships: Partnership[] = [];
        await Promise.allSettled(
          mps.map(async (mp) => {
            try {
              const ps = await partnershipsApiService.getPartnershipsByModpack(mp.id);
              allPartnerships.push(...(Array.isArray(ps) ? ps : []));
            } catch {
              // ignore individual failures
            }
          })
        );
        setPartnerships(allPartnerships);
      }
    };

    load()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [packName, packSlug, mpiId, canView]);

  if (!canView) return null;

  const STATUS_COLORS: Record<string, string> = {
    of_interest: 'bg-violet-500/15 text-violet-300 border-violet-500/25',
    prospecting: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
    outreach: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/25',
    negotiating: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
    active: 'bg-green-500/15 text-green-300 border-green-500/25',
    on_hold: 'bg-gray-500/15 text-gray-300 border-gray-500/25',
    paused: 'bg-gray-500/15 text-gray-300 border-gray-500/25',
    declined: 'bg-red-500/15 text-red-300 border-red-500/25',
    ended: 'bg-red-500/15 text-red-300 border-red-500/25',
  };

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Partnerships"
        icon={Handshake}
        iconClassName="text-blue-400"
        count={!loading && partnerships.length > 0 ? partnerships.length : undefined}
        actions={
          <>
            {canCreate && onCreatePartnership && (
              <button onClick={onCreatePartnership} className="glass-button-sm">
                <Plus className="w-3.5 h-3.5" />
                Prospect
              </button>
            )}
            <Link to="/partnerships" className="glass-button-secondary btn-xs">
              <ExternalLink className="w-3 h-3" />
              View All
            </Link>
          </>
        }
      />

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          <SkeletonLoader className="h-14 w-full rounded-lg" />
          <SkeletonLoader className="h-14 w-full rounded-lg" />
        </div>
      ) : partnerships.length === 0 && modpacks.length === 0 ? (
        <div className="space-y-2">
          <p className="text-sm text-glass-400">
            No partnerships found for &ldquo;{packName}&rdquo;.
            {canCreate && ' Click "Prospect" to start one.'}
          </p>
          {cduServers.length > 0 && (
            <p className="text-xs text-glass-500">
              Note: {cduServers.length} CDU server{cduServers.length > 1 ? 's' : ''} running this pack
              ({cduServers.map((s) => s.clusterName).join(', ')}) — a partnership could be valuable.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {/* Show matched partnership modpacks */}
          {modpacks.length > 0 && partnerships.length === 0 && (
            <p className="text-xs text-glass-400">
              Found {modpacks.length} matching modpack{modpacks.length > 1 ? 's' : ''} in the PR system, but no active partnerships yet.
            </p>
          )}

          {/* Show CDU server context if any */}
          {cduServers.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5 text-xs">
              <span className="text-glass-400">Running on CDU:</span>
              {cduServers.map((s) => (
                <span
                  key={s.serverId}
                  className="badge badge-success"
                >
                  {s.clusterName}
                </span>
              ))}
            </div>
          )}

          {/* Show active partnerships */}
          {partnerships.map((p) => (
            <div
              key={p.id}
              className="subcard p-3 cursor-pointer hover:border-glass-200/25 transition-colors"
              onClick={() => navigate(`/partnerships/${p.id}`)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'badge badge-dot capitalize',
                    STATUS_COLORS[p.status] || 'bg-gray-500/15 text-gray-300',
                  )}>
                    {p.status.replace(/_/g, ' ')}
                  </span>
                  <span className="text-xs text-glass-400 tabular-nums">Priority {p.priority}/10</span>
                </div>
                <span className="text-xs text-glass-500 tabular-nums">
                  {formatDistanceToNow(new Date(p.created_at), { addSuffix: true })}
                </span>
              </div>
              {p.summary && (
                <p className="text-sm text-glass-300 mt-1 line-clamp-2">{p.summary}</p>
              )}
              {p.assigned_to && (
                <div className="flex items-center gap-1 mt-1.5 text-xs text-glass-400">
                  <User className="w-3 h-3" />
                  <CreatorProfile creatorId={p.assigned_to} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

PartnershipsIntegration.displayName = 'PartnershipsIntegration';

// ── Build Team Sub-Section ──────────────────────────────────────────────────

const BuildTeamIntegration = memo(({
  packName,
  canView,
}: {
  packName: string;
  canView: boolean;
}) => {
  const navigate = useNavigate();
  const [modpackLinks, setModpackLinks] = useState<BuildModpackLink[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canView) return;
    setLoading(true);
    // Search for build team modpack links matching this pack name
    buildTeamApiService.getModpackLinksByName(packName)
      .then((results) => {
        setModpackLinks(Array.isArray(results) ? results : []);
      })
      .catch(() => setModpackLinks([]))
      .finally(() => setLoading(false));
  }, [packName, canView]);

  if (!canView) return null;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Build Team Projects"
        icon={Hammer}
        iconClassName="text-orange-400"
        count={!loading && modpackLinks.length > 0 ? modpackLinks.length : undefined}
        actions={
          <Link to="/build-team" className="glass-button-secondary btn-xs">
            <ExternalLink className="w-3 h-3" />
            View All
          </Link>
        }
      />

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          <SkeletonLoader className="h-14 w-full rounded-lg" />
        </div>
      ) : modpackLinks.length === 0 ? (
        <p className="text-sm text-glass-400">
          No build team projects linked to &ldquo;{packName}&rdquo;.
        </p>
      ) : (
        <div className="space-y-2">
          {modpackLinks.map((link) => (
            <div
              key={link.id}
              className="subcard p-3 cursor-pointer hover:border-glass-200/25 transition-colors"
              onClick={() => {
                if (link.target_type === 'project') {
                  navigate(`/build-team/projects/${link.target_id}`);
                } else {
                  navigate(`/build-team/builds/${link.target_id}`);
                }
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-white truncate">{link.modpack_name}</span>
                  {link.modpack_version && (
                    <span className="text-xs font-mono text-glass-400">v{link.modpack_version}</span>
                  )}
                  <span className="chip capitalize">
                    {link.target_type}
                  </span>
                </div>
                <span className="text-xs text-glass-500 tabular-nums shrink-0">
                  {formatDistanceToNow(new Date(link.linked_at), { addSuffix: true })}
                </span>
              </div>
              {link.notes && (
                <p className="text-xs text-glass-400 mt-1 line-clamp-2">{link.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

BuildTeamIntegration.displayName = 'BuildTeamIntegration';

// ── Main Composite Component ────────────────────────────────────────────────

/**
 * The live servers running this pack.
 *
 * Joined on modpack_index_id, which is the same identifier the catalogue keys
 * on -- so a pack page can show where it is actually running, and a config can
 * be opened without going looking for it.
 */
const ModpackConfigsIntegration = memo(({
  mpiId,
  canView,
}: {
  mpiId: number | null | undefined;
  canView: boolean;
}) => {
  const [configs, setConfigs] = useState<ModpackConfig[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canView || !mpiId) return;
    setLoading(true);
    modpackConfigsApiService.listConfigs('production')
      .then((response) => {
        setConfigs(response.results.filter((c) => c.modpack_index_id === mpiId));
      })
      .catch(() => setConfigs([]))
      .finally(() => setLoading(false));
  }, [mpiId, canView]);

  if (!canView) return null;

  return (
    <div className="glass-card p-4 md:p-5">
      <SectionHeader
        title="Servers running this pack"
        icon={Server}
        iconClassName="text-accent-300"
        count={!loading && configs.length > 0 ? configs.length : undefined}
        actions={
          <Link to="/modpack-configs" className="glass-button-secondary btn-xs">
            <ExternalLink className="w-3 h-3" />
            View All
          </Link>
        }
      />

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          <SkeletonLoader className="h-14 w-full rounded-lg" />
        </div>
      ) : !mpiId ? (
        <p className="text-xs text-glass-400">
          This pack has no Modpack Index id, so it cannot be matched to a server config.
        </p>
      ) : configs.length === 0 ? (
        <p className="text-xs text-glass-400">
          No server on the network is configured with this pack.
        </p>
      ) : (
        <div className="divide-y divide-glass-200/10">
          {configs.map((config) => (
            <Link
              key={config.id}
              to={`/modpack-configs/${encodeURIComponent(config.server_address)}`}
              className="flex items-center justify-between gap-3 py-2.5 group"
            >
              <span className="min-w-0">
                <span className="block font-mono text-sm text-white truncate group-hover:text-accent-300">
                  {config.server_address}
                </span>
                <span className="block text-xs text-glass-400">
                  {[config.cluster_name, config.region_name].filter(Boolean).join(' · ') || 'no cluster'}
                </span>
              </span>
              <span className="font-mono tabular-nums text-xs text-glass-300 shrink-0">
                {config.modpack_version || '—'}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
});

export const PackCrossModuleSection = memo(({
  packName,
  packSlug,
  mpiId: _mpiId,
  onCreatePartnership,
  onCreateModpackNote,
}: PackCrossModuleSectionProps) => {
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;

  const canViewModpackNotes = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_read') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canWriteModpackNotes = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_write') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const partnershipPerms = useMemo(() => getPartnershipPerms(identity, isDev), [identity, isDev]);
  const canViewPartnerships = partnershipPerms.canView;
  const canCreatePartnership = partnershipPerms.canEdit;

  const canViewConfigs = useMemo(
    () => isDev || canViewModpackConfigs(identity),
    [identity, isDev],
  );

  const canViewBuildTeam = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_build_panel') || groups.includes('argus_build_lead') || groups.includes('argus_build_admin') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  // At least one integration must be visible
  const hasAnyIntegration = canViewModpackNotes || canViewPartnerships || canViewBuildTeam || canViewConfigs;

  if (!hasAnyIntegration) return null;

  return (
    <div className="space-y-4">
      <ModpackNotesIntegration
        packName={packName}
        packSlug={packSlug}
        canView={canViewModpackNotes}
        canWrite={canWriteModpackNotes}
        onCreateNote={onCreateModpackNote}
      />
      <PartnershipsIntegration
        packName={packName}
        packSlug={packSlug}
        mpiId={_mpiId}
        canView={canViewPartnerships}
        canCreate={canCreatePartnership}
        onCreatePartnership={onCreatePartnership}
      />
      <BuildTeamIntegration
        packName={packName}
        canView={canViewBuildTeam}
      />
      <ModpackConfigsIntegration
        mpiId={_mpiId}
        canView={canViewConfigs}
      />
    </div>
  );
});

PackCrossModuleSection.displayName = 'PackCrossModuleSection';
